import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useOrdersByMemberId } from 'tripPlanner/hooks/api/order'
import {
  isOrderEligibleForChatTrip,
  isOrderSupportedForChatBookingAddition,
} from 'tripPlanner/hooks/orderChatTripEligibility'
import { getFutureFilteredOrdersForChat } from 'tripPlanner/utils/summary'
import {
  getSelectableBookings,
  type AddBookingsMessageInput,
  type AddBookingsOutcome,
  type SelectedBooking,
} from './addBookingsMessage'
import AddBookingModalItem from './AddBookingModalItem'

interface Props {
  onSubmitBookings: (input: AddBookingsMessageInput) => Promise<AddBookingsOutcome>
  disabledBookingItemIds: ReadonlyArray<string>
  isSubmitLocked?: boolean
}

function updateSelectedBookings(
  previous: Array<SelectedBooking>,
  nextSelection: SelectedBooking,
  checked: boolean,
): Array<SelectedBooking> {
  if (checked) {
    if (
      previous.some(
        (selectedBooking) =>
          selectedBooking.selectionKey === nextSelection.selectionKey,
      )
    ) {
      return previous
    }

    return [...previous, nextSelection]
  }

  return previous.filter(
    (selectedBooking) =>
      selectedBooking.selectionKey !== nextSelection.selectionKey,
  )
}

function AddBookingModal({
  onSubmitBookings,
  disabledBookingItemIds,
  isSubmitLocked = false,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const modalElement = useModalElementContext()

  const memberId = useAppSelector(state => state.auth.account.memberId)

  const closeModal = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const [selectedBookings, setSelectedBookings] = useState<Array<SelectedBooking>>(
    [],
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: orders, isLoading: isOrdersLoading } = useOrdersByMemberId(
    currentRegionCode,
    memberId,
  )

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersForChat(orders),
    [orders],
  )

  const supportedOrders = useMemo(
    () =>
      filteredOrders.filter(
        (order) =>
          isOrderEligibleForChatTrip(order) &&
          isOrderSupportedForChatBookingAddition(order),
      ),
    [filteredOrders],
  )

  const supportedOrderBookings = useMemo(
    () => supportedOrders.map(order => ({ order, bookings: getSelectableBookings(order) })),
    [supportedOrders],
  )

  const disabledBookingItemIdSet = useMemo(
    () => new Set(disabledBookingItemIds),
    [disabledBookingItemIds],
  )

  useEffect(() => {
    setSelectedBookings((previous) =>
      previous.filter(
        (selectedBooking) =>
          !disabledBookingItemIdSet.has(selectedBooking.bookingItemId),
      ),
    )
  }, [disabledBookingItemIdSet])

  const handleOnSelect = useCallback((selection: SelectedBooking, checked: boolean) => {
    if (disabledBookingItemIdSet.has(selection.bookingItemId)) {
      return
    }

    setSelectedBookings((previous) => updateSelectedBookings(previous, selection, checked))
  }, [disabledBookingItemIdSet])

  const isLoadingInProgress = isOrdersLoading || isSubmitting

  const failedToAddBookingsMessage = intl.formatMessage({
    id: '-UYQZAC-sYzCfGLe3368',
    defaultMessage: 'We couldn’t add those bookings. Please try again.',
    description: 'Error snackbar message shown when the add bookings chat modal fails to send the selected bookings into the trip planner conversation',
  })

  const handlePrimaryActionClick = useCallback(async() => {
    setIsSubmitting(true)
    const outcome = await onSubmitBookings({ selectedBookings, originalOrders: supportedOrders })
    setIsSubmitting(false)

    if (outcome === 'unavailable') {
      // Keep the modal open so the traveller can retry.
      showSnackbar(failedToAddBookingsMessage, 'critical')
      return
    }
    closeModal()
  }, [closeModal, failedToAddBookingsMessage, onSubmitBookings, selectedBookings, supportedOrders])

  const modalTitle = intl.formatMessage({
    id: 'IdzROLFsTNE1vOMWrIqd',
    defaultMessage: 'Add bookings to trip',
    description: 'Title for add bookings to trip modal in the trip planner feature',
  })

  const noEligibleBookingsHeading = intl.formatMessage({
    id: '6MdYdPMUKtpQBQXo3mii',
    defaultMessage: 'Looks like you have no upcoming eligible bookings',
    description: 'Heading shown in the add booking chat modal when the member has no supported upcoming bookings available to add to their trip',
  })

  const noEligibleBookingsDescription = intl.formatMessage({
    id: '9G5WGTpW6SCVsVIKYpZ8',
    defaultMessage: 'Upcoming hotel, flight, and experience bookings will appear here.',
    description: 'Supporting description shown in the add booking chat modal empty state when there are no supported upcoming bookings to display',
  })

  const continueButtonText = intl.formatMessage({
    id: 'w0bZO-QKmPHt1LUqlgcZ',
    defaultMessage: 'Continue',
    description: 'Button text to continue to the next step in the current multi-step process',
  })

  const cancelButtonText = intl.formatMessage({
    id: '0h3WSfyICL6Yx-LmqJ1k',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel the current action and dismiss the dialog or modal',
  })

  return (
    <Modal
      size="S"
      title={modalTitle}
      primaryActionText={continueButtonText}
      secondaryActionText={cancelButtonText}
      isPrimaryActionDisabled={
        selectedBookings.length === 0 || isOrdersLoading || isSubmitting || isSubmitLocked
      }
      onPrimaryActionClick={handlePrimaryActionClick}
      onSecondaryActionClick={closeModal}
      onClose={closeModal}
    >
      {isLoadingInProgress && <LoadingIndicator inline />}
      {!isLoadingInProgress && (
        <VerticalSpacer gap={12} as="ul">
          {supportedOrderBookings.map(({ order: upcomingOrder, bookings }) => (
            <Group key={upcomingOrder.id} as="li" direction="vertical" gap={12}>
              {bookings.map((booking) => (
                <AddBookingModalItem
                  key={booking.item.id}
                  order={upcomingOrder}
                  booking={booking}
                  disabled={disabledBookingItemIdSet.has(booking.item.id)}
                  onSelect={handleOnSelect}
                />
              ))}
            </Group>
          ))}
        </VerticalSpacer>
      )}
      {!isLoadingInProgress && supportedOrders.length === 0 && (
        <>
          <Heading variant="heading6">
            {noEligibleBookingsHeading}
          </Heading>
          <BodyText variant="large">
            {noEligibleBookingsDescription}
          </BodyText>
        </>
      )}
    </Modal>
  )
}

export default AddBookingModal
